// src/pages/Projects/ProjectEnvironmentList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Project Environments (Project_Environment_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via projectEnvironmentService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import projectEnvironmentService from '../../api/projectEnvironmentService';
import projectService from '../../api/projectService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function ProjectEnvironmentList() {
  const [projects, setProjects] = useState([]);

  // Fetch projects to map names and populate select dropdown options
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(projectEnvironmentService, {
    idKey: 'environmentID',
    itemName: 'Project Environment',
    initialSort: { key: 'environmentID', direction: 'asc' },
  });

  // Schema configuration for Project_Environment_TBL
  const environmentFields = useMemo(() => [
    {
      name: 'environmentID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'environmentName',
      label: 'Environment Name',
      type: 'select',
      required: true,
      options: [
        { label: 'DEV', value: 'DEV' },
        { label: 'UAT', value: 'UAT' },
        { label: 'Prod', value: 'Prod' },
      ],
    },
    {
      name: 'isDefaultFlag',
      label: 'Is Default Flag',
      type: 'checkbox',
      required: false,
      placeholder: 'Flag as default environment for this project',
      defaultValue: false,
    },
  ], [projects]);

  const columns = [
    { key: 'environmentID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? `${proj.projectCode} — ${proj.projectName}` : `-`;
      },
    },
    { key: 'environmentName', label: 'Environment Name', sortable: true },
    {
      key: 'isDefaultFlag',
      label: 'Is Default Flag',
      sortable: true,
      render: (row) => row.isDefaultFlag ? (
        <span style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
          Default
        </span>
      ) : (
        <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
          Standard
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Project Environments</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Configurable list of environments (DEV, UAT, Prod, etc.) per Project.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Environment
        </button>
      </div>

      {/* Main Grid Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="environmentID"
        emptyMessage="No project environments found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={environmentFields}
          initialValues={selectedRecord || { projectID: '', environmentName: '', isDefaultFlag: false }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Project Environment"
        />
      )}

      {/* Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Project Environment"
        itemLabel={selectedRecord ? `${selectedRecord.environmentName} Environment` : 'Project Environment'}
        deleting={deleting}
      />
    </div>
  );
}
